import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'rmh_admin_token';

export const getAdminToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

export const setAdminToken = (token) => {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage unavailable */
  }
};

export const clearAdminToken = () => {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
};

export const http = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  timeout: 20000,
});

http.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ----------------------------------------------------------------- public */

export const createLead = async (payload) => {
  const { data } = await http.post('/leads', payload);
  return data;
};

export const saveAiLead = async (payload) => {
  const { data } = await http.post('/chat/lead', payload);
  return data;
};

export const summarizeConversation = async (sessionId) => {
  const { data } = await http.post('/chat/summarize', { session_id: sessionId });
  return data;
};

/* ------------------------------------------------------------------ admin */

export const adminLogin = async (password) => {
  const { data } = await http.post('/admin/login', { password });
  return data;
};

export const listLeads = async (params = {}) => {
  const { data } = await http.get('/leads', { params });
  return data;
};

export const adminStats = async () => {
  const { data } = await http.get('/admin/stats');
  return data;
};

export const adminLeadDetail = async (id) => {
  const { data } = await http.get(`/admin/leads/${id}`);
  return data;
};

export const adminUpdateLead = async (id, patch) => {
  const { data } = await http.patch(`/admin/leads/${id}`, patch);
  return data;
};

/** Downloads the CSV through an authenticated request. */
export const downloadLeadsCsv = async () => {
  const res = await http.get('/leads/export', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'romilda-leads.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/* ------------------------------------------------------------------- chat */

/**
 * Stream Romilda AI's reply over SSE.
 * Uses fetch (not axios) so we can read the body incrementally.
 *
 * @returns {Promise<string>} the full assembled reply
 */
export async function streamChat({
  sessionId,
  message,
  history,
  lang = 'en',
  onDelta,
  signal,
}) {
  const res = await fetch(`${API}/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, message, history, lang }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Chat request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  let errorMessage = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';

    for (const frame of frames) {
      let event = 'message';
      const dataLines = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (!dataLines.length) continue;
      let payload;
      try {
        payload = JSON.parse(dataLines.join('\n'));
      } catch {
        continue;
      }
      if (event === 'delta' && payload.text) {
        full += payload.text;
        onDelta?.(payload.text, full);
      } else if (event === 'done') {
        if (payload.text) full = payload.text;
      } else if (event === 'error') {
        errorMessage = payload.message || 'Romilda AI is unavailable right now.';
      }
    }
  }

  if (errorMessage) {
    const err = new Error(errorMessage);
    err.friendly = errorMessage;
    throw err;
  }

  return full;
}
